import { createContext, useContext, useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const EmergencyContext = createContext(null);

const ALERT_RADIUS_MILES = 5;

const getDistanceMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export function EmergencyProvider({ children }) {
  const { orgId, userProfile, org } = useAuth();
  const [orgEmergency, setOrgEmergency] = useState(null);
  const [communityEmergency, setCommunityEmergency] = useState(null);
  const [userLocation, setUserLocation] = useState(null);

  // Get user's current location for community alert proximity
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watcher = navigator.geolocation.watchPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => console.error('Geolocation error:', err),
      { enableHighAccuracy: true, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watcher);
  }, []);

  // Listen for org-level emergencies
  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, 'organisations', orgId, 'emergencies'),
      where('status', '==', 'active')
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const emergency = { id: snap.docs[0].id, ...snap.docs[0].data() };
        setOrgEmergency(emergency);
      } else {
        setOrgEmergency(null);
      }
    });
    return unsubscribe;
  }, [orgId]);

  // Listen for global community emergencies within radius
  useEffect(() => {
    // The public verify page mounts this provider with no signed-in user,
    // so there is nothing to subscribe to and Firestore would refuse anyway.
    if (!orgId || !userLocation) return;
    const q = query(
      collection(db, 'emergencies'),
      where('status', '==', 'active')
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const nearby = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(e => {
          // Don't show community alert for own org — already handled by org alert
          if (e.orgId === orgId) return false;
          if (!e.location?.lat || !e.location?.lng) return false;
          const distance = getDistanceMiles(
            userLocation.lat,
            userLocation.lng,
            e.location.lat,
            e.location.lng
          );
          return distance <= ALERT_RADIUS_MILES;
        });
      setCommunityEmergency(nearby.length > 0 ? nearby[0] : null);
    });
    return unsubscribe;
  }, [userLocation, orgId]);

  // Trigger an emergency
  const triggerEmergency = async ({ triggerType, location, machineId, impactForce }) => {
    if (!orgId || !userProfile) return;
    try {
      // Org-level emergency
      const emergencyRef = await addDoc(
        collection(db, 'organisations', orgId, 'emergencies'),
        {
          triggeredBy: userProfile.id,
          triggeredByName: userProfile.name,
          triggeredAt: serverTimestamp(),
          triggerType,
          location: location || null,
          machineId: machineId || null,
          impactForce: impactForce || null,
          status: 'active',
          countdown: 60,
          call999FiredAt: null,
          resolvedAt: null,
          resolvedBy: null,
          responders: [],
        }
      );

      // Global community emergency
      await addDoc(collection(db, 'emergencies'), {
        orgId,
        orgName: org?.name || '',
        productType: 'ops',
        triggeredBy: userProfile.id,
        triggeredByName: userProfile.name,
        triggeredAt: serverTimestamp(),
        location: location || null,
        radius: ALERT_RADIUS_MILES,
        status: 'active',
        resolvedAt: null,
      });

      return emergencyRef.id;
    } catch (err) {
      console.error('triggerEmergency error:', err);
    }
  };

  // Resolve an emergency
  const resolveEmergency = async (emergencyId) => {
    if (!orgId || !userProfile) return;
    try {
      await updateDoc(
        doc(db, 'organisations', orgId, 'emergencies', emergencyId),
        {
          status: 'resolved',
          resolvedAt: serverTimestamp(),
          resolvedBy: userProfile.name,
        }
      );
    } catch (err) {
      console.error('resolveEmergency error:', err);
    }
  };

  // Add responder
  const respondToEmergency = async (emergencyId) => {
    if (!orgId || !userProfile) return;
    try {
      const emergency = orgEmergency || communityEmergency;
      if (!emergency) return;
      const responders = emergency.responders || [];
      if (responders.find(r => r.userId === userProfile.id)) return;
      await updateDoc(
        doc(db, 'organisations', orgId, 'emergencies', emergencyId),
        {
          responders: [
            ...responders,
            {
              userId: userProfile.id,
              name: userProfile.name,
              respondedAt: new Date().toISOString(),
              distanceMiles: userLocation
                ? getDistanceMiles(
                    userLocation.lat,
                    userLocation.lng,
                    emergency.location?.lat,
                    emergency.location?.lng
                  ).toFixed(1)
                : null,
            },
          ],
        }
      );
    } catch (err) {
      console.error('respondToEmergency error:', err);
    }
  };

  const isEmergencyActive = !!orgEmergency || !!communityEmergency;

  const value = {
    orgEmergency,
    communityEmergency,
    isEmergencyActive,
    userLocation,
    triggerEmergency,
    resolveEmergency,
    respondToEmergency,
  };

  return (
    <EmergencyContext.Provider value={value}>
      {children}
    </EmergencyContext.Provider>
  );
}

export function useEmergency() {
  const context = useContext(EmergencyContext);
  if (!context) throw new Error('useEmergency must be used within EmergencyProvider');
  return context;
}